import { Fragment, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { namespaces } from "../consts/i18n";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import NavBar from "../components/NavBar";
import PageContent from "../components/PageContent";
import Page from "../components/Page";
import ProjectCard from "../components/cards/ProjectCard";
import CardGrid from "../components/cards/CardGrid";
import IconTextButton from "../components/buttons/IconTextButton";
import InputDialog from "../components/dialogs/InputDialog";
import MessageDialog from "../components/dialogs/MessageDialog";
import ShareLinkDialog from "../components/dialogs/ShareLinkDialog";
import { adminNavItem } from "./AdminPage";
import { folderInputProps } from "../utils/fsUtils";
import useAuth from "../hooks/useAuth";
import useLinkAccessCheck from "../hooks/useLinkAccessCheck";
import EditProjectDialog from "../components/dialogs/EditProjectDialog";
import RecordListDialog from "../components/dialogs/RecordListDialog";
import {
  useRenameProjectMutation,
  useGetClientQuery,
  useDeleteProjectMutation,
} from "../api/managementApi";

const ClientPage = () => {
  useLinkAccessCheck();

  const { t } = useTranslation(namespaces.pages);
  const { clientId } = useParams();

  const { token, isDemo, isAdmin } = useAuth();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [editProjectDialogOpen, setEditProjectDialogOpen] = useState(false);

  const {
    data: client,
    refetch,
    isFetching: isClientFetching,
    isLoading: isClientLoading,
  } = useGetClientQuery({
    id: clientId,
  });

  const projects = client?.projects;

  const navItems = [
    token && adminNavItem,
    {
      name: client?.name,
      link: `/clients/${clientId}`,
    },
  ].filter(Boolean);

  const [renameProject] = useRenameProjectMutation();

  const [deleteProject] = useDeleteProjectMutation();

  const handleAddProject = () => {
    setCreateDialogOpen(true);
  };

  const [selectedProject, setSelectedProject] = useState(null);

  const [recordListDialogOpen, setRecordListDialogOpen] = useState(false);

  const navigate = useNavigate();

  const handleDeleteClick = (project) => {
    setSelectedProject(project);
    setDeleteDialogOpen(true);
  };

  const handleShowRecordListClick = (project) => {
    setSelectedProject(project);
    setRecordListDialogOpen(true);
  };

  const handleRenameClick = useCallback(
    (project) => {
      setSelectedProject(project);
      if (isAdmin) setEditProjectDialogOpen(true);
      else setRenameDialogOpen(true);
    },
    [isAdmin],
  );

  const handleClick = (project) => {
    navigate(`projects/${project.id}`);
  };

  const handleShareClick = (project) => {
    setSelectedProject(project);
    setShareDialogOpen(true);
  };

  const handleRenameSubmit = async (name) => {
    await renameProject({
      clientId: clientId,
      id: selectedProject.id,
      name,
    }).unwrap();

    refetch();

    setRenameDialogOpen(false);
  };

  const handleDeleteSubmit = async () => {
    await deleteProject({
      clientId: clientId,
      id: selectedProject.id,
    }).unwrap();

    refetch();

    setDeleteDialogOpen(false);
  };

  return (
    <Page>
      <NavBar items={navItems} />
      <PageContent
        title={client?.name}
        actions={[
          /*token &&  user can too create projects, but cant delete them*/ isAdmin && (
            <IconTextButton
              icon="/images/plus.svg"
              label={t("clientPage.addProject")}
              onClick={handleAddProject}
              disabled={isDemo}
            />
          ),
        ]}
      >
        <CardGrid isLoading={isClientLoading || isClientFetching}>
          {projects?.length > 0 &&
            projects.map((project) => (
              <Fragment key={project.id}>
                <ProjectCard
                  project={project}
                  onEdit={handleRenameClick}
                  onDelete={token && handleDeleteClick}
                  onClick={handleClick}
                  onShareClick={handleShareClick}
                  onOpenRecords={handleShowRecordListClick}
                />
              </Fragment>
            ))}
        </CardGrid>
      </PageContent>

      {recordListDialogOpen && (
        <RecordListDialog
          projectId={selectedProject?.id}
          onClose={() => setRecordListDialogOpen(false)}
        />
      )}
      <InputDialog
        isOpen={renameDialogOpen}
        onCancel={() => {
          setRenameDialogOpen(false);
        }}
        initialValue={selectedProject?.name}
        label={t("clientPage.renameProjectDialog.name")}
        title={t("clientPage.renameProjectDialog.title")}
        submitText={t("clientPage.renameProjectDialog.rename")}
        onSubmit={handleRenameSubmit}
        inputProps={folderInputProps}
        disabled={isDemo}
      />
      <EditProjectDialog
        isOpen={editProjectDialogOpen}
        project={selectedProject}
        onCancel={() => {
          setEditProjectDialogOpen(false);
        }}
        onSubmit={() => {
          setEditProjectDialogOpen(false);
          refetch();
        }}
      />
      <EditProjectDialog
        clientId={clientId}
        isCreate={true}
        isOpen={createDialogOpen}
        onCancel={() => {
          setCreateDialogOpen(false);
        }}
        onSubmit={() => {
          setCreateDialogOpen(false);
          refetch();
        }}
      />
      <MessageDialog
        isOpen={deleteDialogOpen}
        title={t("clientPage.deleteProjectDialog.title", {
          folder: selectedProject?.name,
        })}
        onCancel={() => setDeleteDialogOpen(false)}
        onSubmit={handleDeleteSubmit}
        cancelText={t("clientPage.deleteProjectDialog.cancel")}
        submitText={t("clientPage.deleteProjectDialog.delete")}
        disabled={isDemo}
      />
      {selectedProject && shareDialogOpen && (
        <ShareLinkDialog
          disabled={isDemo}
          deactivateDisabled={isDemo}
          isOpen={shareDialogOpen}
          data={selectedProject}
          title={selectedProject.name}
          onClose={() => setShareDialogOpen(false)}
        />
      )}
    </Page>
  );
};

export default ClientPage;
